import { InjectionToken, Signal, inject } from '@angular/core';

import { TableColumn, TableTypeNames } from '../column.service';
import { StoredSortState } from '../filter-sort-persistence.service';
import { TableFilters } from '../../models/entities/table-filters';
import { PersistenceTier, TablePreferenceConcern } from './table-preference-tiers';
import { DefaultTablePreferencesStore } from './default-table-preferences-store.service';

// Tier types/values (PersistenceTier, TablePreferenceConcern, resolveTier, TABLE_PREFERENCE_TIERS)
// have ONE home: ./table-preference-tiers. Import them from there — this contract file does not
// re-export them (avoids two import paths for the same symbols).

/**
 * SD-7878 — the single seam for per-user table preference persistence.
 *
 * Unifies the two persistence services that exist today behind one contract, so the long-term
 * migration from localStorage to the backend user-preferences service is a change in ONE place,
 * tracked centrally. This is the go-forward foundation for v2-layout table preferences.
 *
 * **Signal-first surface.** Reads return `Signal`s so consumers get reactive, zoneless-ready state
 * with no RxJS boilerplate; writes update those signals and write through to the underlying store.
 * RxJS consumers keep working via `toObservable(store.sort(table))` (rxjs-interop).
 *
 * Today the picture is split (columns -> backend, sort/filter/search -> localStorage); the store
 * consults `resolveTier` per (table, concern) and `TABLE_PREFERENCE_TIERS` is the migration tracker.
 */
export interface TablePreferencesStore {
    // --- sort ---
    sort(table: TableTypeNames): Signal<StoredSortState | null>;
    setSort(table: TableTypeNames, sort: StoredSortState | null): void;

    // --- search ---
    search(table: TableTypeNames): Signal<string | null>;
    setSearch(table: TableTypeNames, value: string): void;

    // --- filters ---
    filters(table: TableTypeNames): Signal<TableFilters | null>;
    setFilters(table: TableTypeNames, filters: TableFilters): void;

    // --- columns ---
    columns(table: TableTypeNames): Signal<string[] | null>;
    storeColumns(table: TableTypeNames, columns: TableColumn[]): void;
    persistColumns(table: TableTypeNames): void;
    clearColumns(table: TableTypeNames): void;

    /** The storage tier a given concern currently uses for this table. */
    tierFor(table: TableTypeNames, concern: TablePreferenceConcern): PersistenceTier;
}

/**
 * Inject the store via this token. Swapping storage later is a one-line change here (point the
 * factory at a backend-backed implementation) — no consumer touches the change.
 */
export const TABLE_PREFERENCES_STORE = new InjectionToken<TablePreferencesStore>(
    'TablePreferencesStore',
    { providedIn: 'root', factory: () => inject(DefaultTablePreferencesStore) },
);
